import React, { useState } from 'react';
import { GAMES, gameById, gameByRotation, GameDef, GameResult, GameTheme } from './games';

// ── STREAK REWARD SHELL ──────────────────────────────────────────────────────
//
// Shown as a full-screen overlay when a player hits a correct-answer streak.
// Owns the intro / done screens, scoring display, and theming; the actual
// gameplay comes from a GameDef in ./games (rotated so each break feels fresh).
//
// Props:
//   rotation  — which game to show (index into GAMES); ignored if forceGameId set
//   forceGameId — testing backdoor: show a specific game and offer a picker

interface StreakRewardProps {
  theme: GameTheme;
  isKid: boolean;
  rotation: number;
  forceGameId?: string | null;
  onClose: () => void;
}

export default function StreakReward({ theme, isKid, rotation, forceGameId, onClose }: StreakRewardProps) {
  const initialGame = (forceGameId && gameById(forceGameId)) || gameByRotation(rotation);
  const [game, setGame] = useState<GameDef>(initialGame);
  const isTesting = !!forceGameId;
  // Normal play jumps straight into the game (the streak bar's "Play!" was the
  // intent). The intro screen only appears in test mode, where the picker lives.
  const [phase, setPhase] = useState<'intro' | 'playing' | 'done'>(isTesting ? 'intro' : 'playing');
  const [result, setResult] = useState<GameResult | null>(null);

  const startGame = () => {
    setResult(null);
    setPhase('playing');
  };

  const handleEnd = (r: GameResult) => {
    setResult(r);
    setPhase('done');
  };

  const overlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: `${theme.bg}f2`,
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  };

  const panel: React.CSSProperties = {
    background: theme.card,
    border: `3px solid ${theme.accent}`,
    borderRadius: '20px',
    boxShadow: `0 10px 40px ${theme.accent}44`,
    width: '100%',
    maxWidth: '560px',
    padding: '20px',
    textAlign: 'center',
    color: theme.text,
  };

  const bigBtn: React.CSSProperties = {
    padding: '16px 30px',
    borderRadius: '16px',
    background: theme.btn,
    color: '#fff',
    fontWeight: 800,
    fontSize: isKid ? '1.4rem' : '1.1rem',
    border: 'none',
    cursor: 'pointer',
    minHeight: '56px',
    marginTop: '14px',
  };

  const outlineBtn: React.CSSProperties = {
    padding: '12px 22px',
    borderRadius: '14px',
    background: 'transparent',
    color: theme.accent,
    fontWeight: 700,
    fontSize: '0.95rem',
    border: `2px solid ${theme.accent}`,
    cursor: 'pointer',
    minHeight: '48px',
    marginTop: '10px',
  };

  // A small game picker, shown only in testing mode.
  const picker = isTesting && (
    <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: `1px dashed ${theme.light}` }}>
      <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '8px', fontWeight: 700 }}>
        🧪 Test mode — pick a game
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
        {GAMES.map(g => (
          <button
            key={g.id}
            onClick={() => { setGame(g); setPhase('intro'); setResult(null); }}
            style={{
              padding: '6px 12px',
              borderRadius: '12px',
              border: `2px solid ${g.id === game.id ? theme.accent : theme.light}`,
              background: g.id === game.id ? theme.accent : 'transparent',
              color: g.id === game.id ? '#fff' : theme.text,
              fontWeight: 600,
              fontSize: '0.82rem',
              cursor: 'pointer',
            }}
          >
            {g.emoji} {g.title}
          </button>
        ))}
      </div>
    </div>
  );

  const GameComponent = game.Component;

  return (
    <div style={overlay}>
      <style>{`
        @keyframes reward-pop-in { 0% { transform: scale(0.2); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes reward-burst { 0% { transform: scale(0.6); opacity: 1; } 100% { transform: scale(2); opacity: 0; } }
        @keyframes reward-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
      `}</style>

      {/* Intro screen only appears in test mode (normal play starts in-game). */}
      {phase === 'intro' && (
        <div style={panel}>
          <div style={{ fontSize: '3rem', animation: 'reward-bob 1.2s ease-in-out infinite' }}>{game.emoji}</div>
          <h2 style={{ fontSize: isKid ? '1.8rem' : '1.5rem', margin: '8px 0', color: theme.accent }}>
            {game.title}
          </h2>
          <p style={{ fontSize: isKid ? '1.2rem' : '1rem', fontWeight: 600, lineHeight: 1.5 }}>
            {game.instructions}
          </p>
          <button style={bigBtn} onClick={startGame}>▶️ Start!</button>
          <div>
            <button style={outlineBtn} onClick={onClose}>Close</button>
          </div>
          {picker}
        </div>
      )}

      {phase === 'playing' && (
        <div style={{ ...panel, padding: '14px' }}>
          <GameComponent theme={theme} isKid={isKid} onEnd={handleEnd} />
        </div>
      )}

      {phase === 'done' && result && (
        <div style={panel}>
          <div style={{ fontSize: '3rem' }}>🏆</div>
          <h2 style={{ fontSize: isKid ? '1.8rem' : '1.5rem', margin: '8px 0', color: theme.accent }}>
            Awesome!
          </h2>
          <p style={{ fontSize: isKid ? '1.4rem' : '1.15rem', fontWeight: 700 }}>
            You got {result.score} {result.noun}! ⭐
          </p>
          <button style={bigBtn} onClick={onClose}>📚 Back to Quiz</button>
          {/* In test mode the picker offers replays; in normal play, more games
              come from banking another streak — no "play again" here. */}
          {isTesting && (
            <div>
              <button style={outlineBtn} onClick={startGame}>🔁 Replay</button>
            </div>
          )}
          {picker}
        </div>
      )}
    </div>
  );
}
